import { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { namespaces, useSharedTranslation } from 'us-react-components/dist/hooks/useSharedTranslation';
import { generatePath, useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChild } from '@fortawesome/pro-solid-svg-icons/faChild';
import { faHeart } from '@fortawesome/pro-solid-svg-icons/faHeart';
import { faPaw } from '@fortawesome/pro-solid-svg-icons/faPaw';
import { faHouse } from '@fortawesome/pro-solid-svg-icons/faHouse';
import { faSchool } from '@fortawesome/pro-solid-svg-icons/faSchool';

import { SitterSearchCard } from 'us-react-components/dist/SitterSearchCard/SitterSearchCard';
import { Modal } from 'us-react-components/dist/Modal/Modal';
import { useSitterSearchCardHandlers } from 'us-react-components/dist/hooks/useSitterSearchCardHandlers';
import { daysOfWeek } from 'us-react-components/dist/constants/DaysOfWeek';
import { localDateTimeInputToApi } from 'us-react-components/dist/utils/date';
import {
  SITTER_SEARCH_TYPES,
  sitterSearchTypeByCode,
} from 'us-react-components/dist/constants/SitterSearchTypes';
import {
  EmailNotificationType,
  PagedResponseMeta,
  Parent,
  SavedSearch,
  SavedSearchService,
  Sitter,
  SitterService,
  SitterSearchAgeExperience,
  SitterSearchLanguage,
  SitterSearchNotificationTypeService,
  SitterSearchRecurring,
  SitterSearchSpecialExperience,
  SitterSearchSpecialService,
  SitterSearchSpecialSituation,
  SitterSearchTeachingSkill,
} from 'us-web-services';

import { Button, ButtonType } from '../../components';
import { Storage } from '../../constants';
import { useDependencies } from '../../dependencies';
import { Routes } from '../../Routes';
import { FilterSidebar } from './components/FilterSidebar';
import { AvailableDateFilter } from './components/AvailableDateFilter';
import { saveSitterSearchNav } from './sitterSearchNav';
import { AgencySitterCardWidget } from './components/AgencySitterCardWidget';
import { OutschoolWidget } from './components/OutschoolWidget';
import { NotEnoughCaregiversCard } from './components/NotEnoughCaregiversCard';
import { CompleteCareToast } from './components/CompleteCareToast';
import {
  HOMEWORK_ASSISTANCE_TUTORING_ID,
  RECURRING_SEARCH_TYPE_CODES,
  VERTICAL_CHILD_CARE_ID,
  VERTICAL_DAY_CARE_ID,
} from './constants';

type SortCode = 'bestMatch' | 'rate' | 'reviews' | 'connections' | 'distance';

const SORT_OPTIONS: Array<{ code: SortCode; labelKey: string; apiValue: string | null; dir: 'ASC' | 'DESC' | null }> = [
  { code: 'bestMatch', labelKey: 'pages.sitters.sort.bestMatch', apiValue: null, dir: null },
  { code: 'rate', labelKey: 'pages.sitters.sort.rate', apiValue: 'rate', dir: 'ASC' },
  { code: 'reviews', labelKey: 'pages.sitters.sort.reviews', apiValue: 'reviews', dir: 'DESC' },
  { code: 'connections', labelKey: 'pages.sitters.sort.connections', apiValue: 'connections', dir: 'DESC' },
  { code: 'distance', labelKey: 'pages.sitters.sort.distance', apiValue: 'distance', dir: 'ASC' },
];

const VERTICAL_TABS: Array<{ id: number; code: string; label: string; icon: typeof faChild }> = [
  { id: 1, code: 'childCare', label: 'Sitters, nannies, & backup care', icon: faChild },
  { id: 7, code: 'dayCare', label: 'Daycares & preschools', icon: faSchool },
  { id: 2, code: 'petCare', label: 'Pet care', icon: faPaw },
  { id: 3, code: 'seniorCare', label: 'Senior care', icon: faHeart },
  { id: 4, code: 'householdServices', label: 'Household services', icon: faHouse },
];

const VERTICAL_BY_ID: Record<number, string> = {
  1: 'Sitters, nannies, & backup care',
  2: 'Pet care',
  3: 'Senior care',
  4: 'Household services',
  7: 'Daycares & preschools',
};

const buildSavedSearchPayload = (
  params: URLSearchParams,
  user: { id?: number },
  parent: Parent | undefined,
  options: {
    title: string;
    notificationType?: EmailNotificationType;
    id?: number;
  },
): SavedSearch => {
  const numParam = (k: string): number | undefined => {
    const v = params.get(k);
    return v != null && v !== '' && !Number.isNaN(Number(v)) ? Number(v) : undefined;
  };
  const boolParam = (k: string): true | undefined =>
    params.get(k) === 'true' ? true : undefined;
  const idsParam = (k: string): number[] => {
    const raw = params.get(k);
    if (!raw) return [];
    return raw.split(',').map(Number).filter((n) => !Number.isNaN(n));
  };
  const stringsParam = (k: string): string[] => {
    const raw = params.get(k);
    if (!raw) return [];
    return raw.split(',').map((s) => s.trim()).filter((s) => s.length > 0);
  };

  const recurring: SitterSearchRecurring[] = daysOfWeek
    .map(({ id, code, label, filterKey }) => ({
      dayOfWeek: { id, code, label },
      timeOfDayIds: idsParam(filterKey),
    }))
    .filter((entry) => entry.timeOfDayIds.length > 0);

  const address = {
    city: params.get('city') ?? parent?.address?.city ?? '',
    state: params.get('state') ?? parent?.address?.state ?? '',
    zip: params.get('zip') ?? parent?.address?.zip ?? '',
    street: params.get('street') ?? parent?.address?.street,
    latitude: numParam('latitude') ?? parent?.address?.latitude,
    longitude: numParam('longitude') ?? parent?.address?.longitude,
  };

  const payload: SavedSearch = {
    title: options.title,
    active: true,
    parentId: user.id,
    verticalTypeId: numParam('verticalTypeId'),
    address,
    distance: numParam('distance') ?? 25,
    numberOfChildren: numParam('numberOfChildren') ?? parent?.kids?.length ?? 1,
    maximumRate: numParam('maximumRate'),
    maxLastAccessDays: numParam('maxLastAccessDays'),
    exactRecurringAvailability: boolParam('exactRecurringAvailability'),
    excludeFavorites: boolParam('excludeFavorites'),
    willSitDog: boolParam('willSitDog'),
    willSitCat: boolParam('willSitCat'),
    willSitOtherPet: boolParam('willSitOtherPet'),
    willSitSenior: boolParam('willSitSenior'),
    willSitHome: boolParam('willSitHome'),
    backgroundCheck: boolParam('backgroundCheck'),
    hasCar: boolParam('hasCar'),
    willingToDriveChildren: boolParam('willingToDriveChildren'),
    respondsQuickly: params.get('maxResponseTime') === '1' ? true : undefined,
    acceptsCreditCard: boolParam('acceptsCreditCard'),
  };

  if (options.id != null) payload.id = options.id;
  if (options.notificationType?.id != null) {
    payload.notificationType = {
      id: options.notificationType.id,
      label: options.notificationType.label,
    };
  }
  if (recurring.length > 0) payload.recurring = recurring;

  const sort = params.get('sort');
  if (sort) payload.sort = sort;

  const searchTypeCode = params.get('searchTypeCode');
  if (searchTypeCode && sitterSearchTypeByCode[searchTypeCode]) {
    payload.type = sitterSearchTypeByCode[searchTypeCode];
  }

  const languageIds = idsParam('languages');
  if (languageIds.length > 0) {
    payload.languages = languageIds.map<SitterSearchLanguage>((languageId) => ({ languageId }));
  }

  const certificationIds = idsParam('certificationIds');
  if (certificationIds.length > 0) {
    payload.specialExperience = certificationIds.map<SitterSearchSpecialExperience>(
      (specialExperienceId) => ({ specialExperienceId }),
    );
  }

  const teachingSkillIds = idsParam('teachingSkillIds');
  if (teachingSkillIds.length > 0) {
    payload.teachingSkill = teachingSkillIds.map<SitterSearchTeachingSkill>(
      (teachingSkillId) => ({ teachingSkillId }),
    );
  }

  const specialSituationIds = idsParam('specialSituationIds');
  if (specialSituationIds.length > 0) {
    payload.specialSituation = specialSituationIds.map<SitterSearchSpecialSituation>(
      (specialSituationId) => ({ specialSituationId }),
    );
  }

  const specialServiceIds = idsParam('specialServiceIds');
  if (specialServiceIds.length > 0) {
    payload.specialService = specialServiceIds.map<SitterSearchSpecialService>(
      (specialServiceId) => ({ specialServiceId }),
    );
  }

  const ageExperienceCodes = stringsParam('ageExperience');
  if (ageExperienceCodes.length > 0) {
    payload.ageExperience = ageExperienceCodes.map<SitterSearchAgeExperience>(
      (label) => ({ label }),
    );
  }

  const startStr = params.get('start');
  const endStr = params.get('end');
  if (startStr) {
    const apiStart = localDateTimeInputToApi(startStr);
    if (apiStart) payload.start = apiStart;
  }
  if (endStr) {
    const apiEnd = localDateTimeInputToApi(endStr);
    if (apiEnd) payload.end = apiEnd;
  }

  return payload;
};

export const Sitters = observer((): JSX.Element => {
  const { t } = useSharedTranslation(namespaces.family);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { stores, services: { tracker } } = useDependencies();
  const user = stores.user.user;

  const parent = stores.parent.parent;

  const [sitters, setSitters] = useState<Sitter[]>([]);
  const [meta, setMeta] = useState<PagedResponseMeta | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [sortCode, setSortCode] = useState<SortCode>(
    (searchParams.get('sort') as SortCode) || 'bestMatch',
  );
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [saveSearchOpen, setSaveSearchOpen] = useState(false);
  const [saveSearchName, setSaveSearchName] = useState('');
  const [saveSearchSubmitting, setSaveSearchSubmitting] = useState(false);
  const [notificationTypes, setNotificationTypes] = useState<EmailNotificationType[]>([]);
  const [selectedNotificationTypeId, setSelectedNotificationTypeId] = useState<number | null>(null);

  const pageParam = Number(searchParams.get('page') || 1) || 1;
  const activeVerticalId = Number(searchParams.get('verticalTypeId')) || null;

  useEffect(() => {
    document.title = 'UrbanSitter - Caregivers';
  }, []);

  // Angular sitter-object-service.js:47 — when the parent finishes recurring
  // signup (Reasons.tsx sets SIGN_UP_RECURRING) and lands on Sitters with an
  // availability filter, prompt them to save the search. Fire once, then clear.
  useEffect(() => {
    const flag = localStorage.getItem(Storage.SIGN_UP_RECURRING);
    if (!flag) return;
    const hasAvailability =
      !!searchParams.get('availability') || !!searchParams.get('availabilityTypes');
    if (hasAvailability) {
      localStorage.removeItem(Storage.SIGN_UP_RECURRING);
      setSaveSearchOpen(true);
    }
  }, [searchParams]);

  useEffect(() => {
    const load = async (): Promise<void> => {
      try {
        const resp = await SitterSearchNotificationTypeService.get();
        const types = (resp.data?.data ?? []) as EmailNotificationType[];
        setNotificationTypes(types);
        if (types[0]?.id) setSelectedNotificationTypeId(types[0].id);
      } catch {
        /* ignore */
      }
    };
    void load();
  }, []);

  // Mirrors Angular sitters.js:561-733 location + vertical fallback chain.
  // Order: URL params → sessionStorage geocodeData/searchBarData → parent.address.
  // Keys come from apps/angular/src/js/ng/common/constants/storage.constant.js
  // (GEOCODE_DATA_KEY='geocodeData', SEARCH_BAR_DATA_KEY='searchBarData').
  useEffect(() => {
    const has = (k: string): boolean => {
      const v = searchParams.get(k);
      return v != null && v !== '';
    };

    const readSession = <T,>(key: string): T | null => {
      try {
        const raw = sessionStorage.getItem(key);
        return raw ? (JSON.parse(raw) as T) : null;
      } catch {
        return null;
      }
    };

    const geocode = readSession<{
      street?: string;
      city?: string;
      state?: string;
      zip?: string;
    }>('geocodeData');
    const searchBar = readSession<{ vertical?: { id?: number; code?: string } }>(
      'searchBarData',
    );

    const hasAnyLocationParam =
      has('city') || has('state') || has('zip') || has('street');
    const hasVerticalParam = has('verticalTypeId');

    const pickLocation = (): {
      street?: string;
      city?: string;
      state?: string;
      zip?: string;
    } | null => {
      if (hasAnyLocationParam) return null;
      if (geocode && geocode.zip && geocode.city && geocode.state) return geocode;
      if (parent?.address) {
        const a = parent.address;
        if (a.city || a.state || a.zip || a.street) return a;
      }
      return null;
    };

    const nextLocation = pickLocation();
    const nextVertical = !hasVerticalParam && searchBar?.vertical?.id
      ? searchBar.vertical
      : null;

    if (!nextLocation && !nextVertical) return;

    setSearchParams(
      (sp) => {
        const next = new URLSearchParams(sp);
        if (nextLocation) {
          if (nextLocation.street) next.set('street', nextLocation.street);
          if (nextLocation.city) next.set('city', nextLocation.city);
          if (nextLocation.state) next.set('state', nextLocation.state);
          if (nextLocation.zip) next.set('zip', nextLocation.zip);
        }
        if (nextVertical?.id) {
          next.set('verticalTypeId', String(nextVertical.id));
          if (nextVertical.code) next.set('verticalTypeCode', nextVertical.code);
        }
        return next;
      },
      { replace: true },
    );
    // Only run once parent loads or when a refresh re-empties the URL.

  }, [parent?.address?.city, parent?.address?.state, parent?.address?.zip, parent?.address?.street]);

  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    setIsLoading(true);

    const sortOption = SORT_OPTIONS.find((o) => o.code === sortCode) ?? SORT_OPTIONS[0];

    const verticalTypeIdParam = Number(searchParams.get('verticalTypeId'));
    const isDaycare = verticalTypeIdParam === VERTICAL_DAY_CARE_ID;

    const readSessionLocation = (): { latitude?: number; longitude?: number } | null => {
      try {
        const raw = sessionStorage.getItem('geocodeData');
        return raw ? (JSON.parse(raw) as { latitude?: number; longitude?: number }) : null;
      } catch {
        return null;
      }
    };

    const urlLat = searchParams.get('latitude');
    const urlLng = searchParams.get('longitude');
    const hasLocationParam =
      !!searchParams.get('city') || !!searchParams.get('state') || !!searchParams.get('zip');

    let resolvedLatitude: number | undefined;
    let resolvedLongitude: number | undefined;
    if (urlLat && urlLng) {
      const lat = Number(urlLat);
      const lng = Number(urlLng);
      if (Number.isFinite(lat) && Number.isFinite(lng)) {
        resolvedLatitude = lat;
        resolvedLongitude = lng;
      }
    }
    if (resolvedLatitude == null && !hasLocationParam) {
      const geocode = readSessionLocation();
      if (geocode?.latitude != null && geocode?.longitude != null) {
        resolvedLatitude = geocode.latitude;
        resolvedLongitude = geocode.longitude;
      } else if (parent?.address?.latitude != null && parent?.address?.longitude != null) {
        resolvedLatitude = parent.address.latitude;
        resolvedLongitude = parent.address.longitude;
      }
    }

    const MAX_KIDS_FOR_SEARCH = 4;
    const numberOfChildrenParam = searchParams.get('numberOfChildren');
    const parsedNumberOfChildren = numberOfChildrenParam != null ? Number(numberOfChildrenParam) : NaN;
    const numberOfChildrenDefault = parent?.kids?.length || 1;
    const numberOfChildren = Math.min(
      Number.isFinite(parsedNumberOfChildren) ? parsedNumberOfChildren : numberOfChildrenDefault,
      MAX_KIDS_FOR_SEARCH,
    );

    const distanceParam = searchParams.get('distance');
    const parsedDistance = distanceParam != null ? Number(distanceParam) : NaN;
    const distance = Number.isFinite(parsedDistance) ? parsedDistance : 25;

    const params: Record<string, unknown> = {
      userId: user.id,
      parentId: user.id,
      page: pageParam,
      limit: isDaycare ? 12 : 10,
      isUserSearch: true,
      distance,
      numberOfChildren,
    };

    if (resolvedLatitude != null && resolvedLongitude != null) {
      params.latitude = resolvedLatitude;
      params.longitude = resolvedLongitude;
    }

    if (sortOption.apiValue) {
      params.sort = sortOption.apiValue;
      params[`${sortOption.apiValue}.dir`] = sortOption.dir;
    }

    const passthrough = [
      'verticalTypeId',
      'availability',
      'availabilityTypes',
      'searchTypeCode',
      'start',
      'end',
      'zip',
      'city',
      'state',
      'street',
      'maximumRate',
      'willSitDog',
      'willSitCat',
      'willSitOtherPet',
      'willSitSenior',
      'willSitHome',
      'backgroundCheck',
      'hasCar',
      'willingToDriveChildren',
      'maxResponseTime',
      'acceptsCreditCard',
      'excludeFavorites',
      'passed',
      'savedSearchId',
      'searchId',
      'jobId',
      'certificationIds',
      'languages',
      'teachingSkillIds',
      'specialSituationIds',
      'specialServiceIds',
      'maxLastAccessDays',
      'collegeId',
      'currentlyAttendingCollege',
      'name',
      'ageRanges',
      'facilityClass',
      'scheduleTypes',
      'sundayAvailability',
      'mondayAvailability',
      'tuesdayAvailability',
      'wednesdayAvailability',
      'thursdayAvailability',
      'fridayAvailability',
      'saturdayAvailability',
      'exactRecurringAvailability',
    ];
    for (const key of passthrough) {
      const val = searchParams.get(key);
      if (val != null && val !== '') params[key] = val;
    }

    const load = async (): Promise<void> => {
      try {
        const resp = await SitterService.getByFilter({ params });
        if (cancelled) return;
        const data = resp.data?.data ?? [];
        const respMeta = resp.data?.meta ?? null;
        setSitters(data);
        setMeta(respMeta);
        // Persist the list so the sitter profile page can render a "Back to
        // search | Previous | Next" strip. Mirrors SitterObjectService.
        const filterQuery = new URLSearchParams();
        for (const [k, v] of searchParams.entries()) {
          if (k !== 'page') filterQuery.set(k, v);
        }
        saveSitterSearchNav({
          ids: data.map((s) => s.id as number).filter((v): v is number => typeof v === 'number'),
          query: filterQuery.toString(),
          page: pageParam,
          pageCount: respMeta?.pageCount ?? 1,
          firstPage: respMeta?.firstPage ?? pageParam <= 1,
          lastPage: respMeta?.lastPage ?? false,
        });
      } catch (err) {
        console.warn('[Sitters] load failed', err);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    void load();

    return (): void => {
      cancelled = true;
    };
  }, [user?.id, pageParam, sortCode, searchParams]);

  const goToPage = (page: number): void => {
    setSearchParams(
      (sp) => {
        const next = new URLSearchParams(sp);
        next.set('page', String(page));
        return next;
      },
      { replace: true },
    );
  };

  const setSort = (code: SortCode): void => {
    setSortCode(code);
    setSearchParams(
      (sp) => {
        const next = new URLSearchParams(sp);
        if (code === 'bestMatch') {
          next.delete('sort');
        } else {
          next.set('sort', code);
        }
        next.set('page', '1');
        return next;
      },
      { replace: true },
    );
  };

  const setSearchType = (code: string): void => {
    setSearchParams(
      (sp) => {
        const next = new URLSearchParams(sp);
        if (!code) {
          next.delete('searchTypeCode');
          next.delete('availability');
        } else {
          next.set('searchTypeCode', code);
          // Angular's setSearchType also populates the `availability` filter so
          // search results honor the picked type. OCCASIONAL clears it.
          if (code === 'FULL_TIME') next.set('availability', 'full_time');
          else if (code === 'PART_TIME') next.set('availability', 'part_time');
          else next.delete('availability');
        }
        next.set('page', '1');
        return next;
      },
      { replace: true },
    );
  };

  const setVerticalTab = (id: number | null): void => {
    const tab = id == null ? null : VERTICAL_TABS.find((v) => v.id === id);
    setSearchParams(
      (sp) => {
        const next = new URLSearchParams(sp);
        if (id == null) {
          next.delete('verticalTypeId');
          next.delete('verticalTypeCode');
        } else {
          next.set('verticalTypeId', String(id));
          if (tab?.code) next.set('verticalTypeCode', tab.code);
          else next.delete('verticalTypeCode');
        }
        next.set('page', '1');
        return next;
      },
      { replace: true },
    );
  };

  const { handleCardClick, handleToggleFavorite, handleBook } = useSitterSearchCardHandlers({
    userId: user?.id,
    track: tracker.track.bind(tracker),
    navigateToSitter: (id) => navigate(generatePath(Routes.sitter.profile, { id: String(id) })),
    navigateToBook: (id) => navigate(`/parent/bookings/select?sitterId=${id}`),
    logPrefix: 'Sitters',
  });

  const availability = searchParams.get('availability');
  const verticalTypeCode = searchParams.get('verticalTypeCode');
  const searchTypeCode = searchParams.get('searchTypeCode');
  const showRecurringPost = availability === 'full_time' || availability === 'part_time';

  const isDaycare = activeVerticalId === VERTICAL_DAY_CARE_ID;
  const isChildCare = activeVerticalId === VERTICAL_CHILD_CARE_ID;
  const hasKinside = stores.parent.hasKinsidePartnership;

  const teachingSkillIds = searchParams.get('teachingSkillIds');
  const specialServiceIds = searchParams.get('specialServiceIds');
  const hasHomeworkTutoring =
    specialServiceIds?.split(',').map(Number).includes(HOMEWORK_ASSISTANCE_TUTORING_ID) ?? false;
  const showOutschool = pageParam === 1 && (!!teachingSkillIds || hasHomeworkTutoring);
  const showAgency =
    pageParam === 1 &&
    isChildCare &&
    (!searchTypeCode || searchTypeCode === 'OCCASIONAL');

  const totalPages = meta?.pageCount ?? 1;
  const hasResults = sitters.length > 0;
  const totalResults = meta?.size ?? sitters.length;
  const isLastPage = meta?.lastPage === true || pageParam >= totalPages;

  const savedSearchIdParam = Number(searchParams.get('savedSearchId') ?? searchParams.get('searchId')) || null;
  const [saveSearchMode, setSaveSearchMode] = useState<'update' | 'create'>(
    savedSearchIdParam ? 'update' : 'create',
  );
  useEffect(() => {
    setSaveSearchMode(savedSearchIdParam ? 'update' : 'create');
  }, [savedSearchIdParam]);

  const saveSearch = async (): Promise<void> => {
    if (!saveSearchName.trim() || !user?.id) return;
    setSaveSearchSubmitting(true);
    try {
      const notificationType = notificationTypes.find(
        (n) => n.id === selectedNotificationTypeId,
      );
      const isUpdate = saveSearchMode === 'update' && savedSearchIdParam != null;
      const payload = buildSavedSearchPayload(searchParams, { id: user.id }, parent, {
        title: saveSearchName.trim(),
        notificationType,
        id: isUpdate ? savedSearchIdParam : undefined,
      });
      if (isUpdate && savedSearchIdParam != null) {
        await SavedSearchService.updateSearch(savedSearchIdParam, payload);
      } else {
        await SavedSearchService.saveSearch(payload);
      }
      toast.success(t('pages.sitters.saveSearchToast.success'));
      setSaveSearchOpen(false);
      setSaveSearchName('');
    } catch {
      toast.error(t('pages.sitters.saveSearchToast.error'));
    } finally {
      setSaveSearchSubmitting(false);
    }
  };

  return (
    <div className='container py-6'>
      <div className='flex flex-wrap items-baseline justify-between mb-4 gap-3'>
        <h1 className='text-2xl font-semibold mb-0'>{t('pages.sitters.heading')}</h1>
        {!isLoading && (
          <span className='text-sm text-neutral-600'>
            {t('pages.sitters.results', { count: totalResults })}
          </span>
        )}
      </div>

      {/* Vertical filter: mobile <select>, desktop pills. */}
      <div className='mb-5 border-b border-neutral-200 pb-3'>
        <select
          aria-label={t('pages.sitters.careTypeAriaLabel')}
          value={activeVerticalId ?? ''}
          onChange={(e): void => {
            const v = e.target.value;
            setVerticalTab(v === '' ? null : Number(v));
          }}
          className='md:hidden w-full border border-neutral-300 rounded px-3 py-2'
        >
          <option value=''>{t('pages.sitters.allCaregivers')}</option>
          {VERTICAL_TABS.map((tab) => (
            <option key={tab.id} value={tab.id}>
              {tab.label}
            </option>
          ))}
        </select>
        <div className='hidden md:flex flex-wrap gap-2'>
          <Button
            onClick={(): void => setVerticalTab(null)}
            className={`px-3 py-1.5 rounded-full text-sm font-medium border transition ${
              activeVerticalId == null
                ? 'bg-sapphire text-white border-sapphire'
                : 'border-neutral-300 hover:bg-neutral-50'
            }`}
          >
            {t('pages.sitters.allCaregivers')}
          </Button>
          {VERTICAL_TABS.map((tab) => {
            const active = activeVerticalId === tab.id;
            return (
              <Button
                key={tab.id}
                onClick={(): void => setVerticalTab(tab.id)}
                className={`px-3 py-1.5 rounded-full text-sm font-medium border transition inline-flex items-center gap-1.5 ${
                  active
                    ? 'bg-sapphire text-white border-sapphire'
                    : 'border-neutral-300 hover:bg-neutral-50'
                }`}
              >
                <FontAwesomeIcon icon={tab.icon} />
                {tab.label}
              </Button>
            );
          })}
        </div>
      </div>

      <div className='flex flex-wrap items-center gap-3 mb-5'>
        <Button
          buttonType={ButtonType.secondary}
          className='btn btn-secondary md:hidden'
          onClick={(): void => setShowMobileFilters((v) => !v)}
        >
          <i className='fa-solid fa-sliders mr-1' />
          Filters
        </Button>

        {!isDaycare && (
          <>
            <label htmlFor='sitters-search-type' className='text-sm font-medium'>
              Type of job:
            </label>
            <select
              id='sitters-search-type'
              value={searchTypeCode ?? ''}
              onChange={(e): void => setSearchType(e.target.value)}
              className='border border-neutral-300 rounded px-3 py-1'
            >
              {SITTER_SEARCH_TYPES.map((o) => (
                <option key={o.code || 'all'} value={o.code}>
                  {o.label}
                </option>
              ))}
            </select>
          </>
        )}

        <label htmlFor='sitters-sort' className='text-sm font-medium'>
          {t('pages.sitters.sortLabel')}:
        </label>
        <select
          id='sitters-sort'
          value={sortCode}
          onChange={(e): void => setSort(e.target.value as SortCode)}
          className='border border-neutral-300 rounded px-3 py-1'
        >
          {SORT_OPTIONS.map((o) => (
            <option key={o.code} value={o.code}>
              {t(o.labelKey)}
            </option>
          ))}
        </select>

        <Button
          buttonType={ButtonType.secondary}
          id='save-search-button'
          className='btn btn-secondary'
          onClick={(): void => setSaveSearchOpen(true)}
        >
          <i className='fa-solid fa-bookmark mr-1' />
          {t('pages.sitters.saveSearchModal.title')}
        </Button>

        {showRecurringPost && (
          <div className='ml-auto text-sm text-right hidden md:block'>
            <h4 className='m-0 font-semibold'>{t('pages.sitters.postSearchLine1')}</h4>
            <div>{t('pages.sitters.postSearchLine2')}</div>
            <a
              id='post-search'
              className='font-bold underline'
              href={`/app/job-post?type=recurring&availability=${availability}&bookingType=${verticalTypeCode ?? ''}`}
            >
              {t('pages.sitters.postAJob')}
            </a>
          </div>
        )}
      </div>

      {!isDaycare && !RECURRING_SEARCH_TYPE_CODES.has(searchTypeCode ?? '') && (
        <AvailableDateFilter />
      )}

      <div className='grid grid-cols-1 md:grid-cols-12 gap-6'>
        <div className={`md:col-span-3 ${showMobileFilters ? 'block' : 'hidden'} md:block`}>
          <FilterSidebar
            onFilterChange={(): void => setShowMobileFilters(false)}
            isDaycare={isDaycare}
            verticalTypeCode={verticalTypeCode}
            searchTypeCode={searchTypeCode}
          />
        </div>
        <div className='md:col-span-9'>
          {isLoading && (
            <div className='text-neutral-600 text-center pt-4'>
              <i className='fa-solid fa-spinner fa-spin mr-2' />
              <span>{t('common.loading')}</span>
            </div>
          )}

          {!isLoading && hasResults && (
            <>
              <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                {sitters.slice(0, 7).map((sitter) => (
                  <SitterSearchCard
                    key={sitter.id}
                    sitter={sitter}
                    userPictureUrl={user?.pictureUrl}
                    onCardClick={handleCardClick}
                    onToggleFavorite={handleToggleFavorite}
                    onBook={handleBook}
                  />
                ))}
                {showAgency && (
                  <AgencySitterCardWidget
                    city={searchParams.get('city') ?? ''}
                    state={searchParams.get('state') ?? ''}
                    street={searchParams.get('street') ?? ''}
                    zipCode={searchParams.get('zip') ?? ''}
                    numberOfChildren={Number(searchParams.get('numberOfChildren')) || 1}
                    startDate={searchParams.get('start') ?? ''}
                    endDate={searchParams.get('end') ?? ''}
                  />
                )}
                {showOutschool && (
                  <div>
                    <OutschoolWidget />
                  </div>
                )}
                {sitters.slice(7).map((sitter) => (
                  <SitterSearchCard
                    key={sitter.id}
                    sitter={sitter}
                    userPictureUrl={user?.pictureUrl}
                    onCardClick={handleCardClick}
                    onToggleFavorite={handleToggleFavorite}
                    onBook={handleBook}
                  />
                ))}
                {isLastPage && (
                  <div className='col-span-1 md:col-span-2'>
                    <NotEnoughCaregiversCard
                      isDaycare={isDaycare}
                      resultCount={totalResults}
                      filter={Object.fromEntries(
                        Array.from(searchParams.entries()).filter(([k]) => k !== 'page'),
                      )}
                    />
                  </div>
                )}
              </div>

              {totalPages > 1 && (
                <div className='flex items-center justify-center gap-3 mt-6'>
                  <Button
                    buttonType={ButtonType.secondary}
                    disabled={pageParam <= 1}
                    onClick={(): void => goToPage(pageParam - 1)}
                  >
                    {t('common.previous')}
                  </Button>
                  <span className='text-sm'>
                    {t('pages.sitters.pageOf', { current: pageParam, total: totalPages })}
                  </span>
                  <Button
                    buttonType={ButtonType.secondary}
                    disabled={pageParam >= totalPages || meta?.lastPage === true}
                    onClick={(): void => goToPage(pageParam + 1)}
                  >
                    {t('common.next')}
                  </Button>
                </div>
              )}
            </>
          )}

          {!isLoading && !hasResults && (
            <div className='grid grid-cols-1 md:grid-cols-2 gap-4 my-4'>
              <div className='us-card rounded p-6 text-center'>
                <h3 className='mt-2 mb-0'>{t('pages.sitters.nullHeading')}</h3>
                <h4 className='text-base font-light mt-2'>{t('pages.sitters.nullBody')}</h4>
                <div className='text-center mt-3'>
                  <a href='/app/job-post' className='btn btn-primary inline-block px-8'>
                    {t('pages.sitters.postToCaregivers')}
                  </a>
                </div>
              </div>
              <NotEnoughCaregiversCard
                isDaycare={isDaycare}
                resultCount={totalResults}
                filter={Object.fromEntries(
                  Array.from(searchParams.entries()).filter(([k]) => k !== 'page'),
                )}
              />
            </div>
          )}
        </div>
      </div>

      {isDaycare && hasKinside && <CompleteCareToast />}

      <Modal
        isOpen={saveSearchOpen}
        onClose={(): void => setSaveSearchOpen(false)}
        title={t('pages.sitters.saveSearchModal.title')}
        className='w-full max-w-2xl'
        footer={
          <>
            <Button
              buttonType={ButtonType.tertiary}
              onClick={(): void => setSaveSearchOpen(false)}
            >
              {t('common.cancel')}
            </Button>
            <Button
              buttonType={ButtonType.primary}
              disabled={saveSearchSubmitting || !saveSearchName.trim()}
              onClick={saveSearch}
            >
              {t('common.save')}
            </Button>
          </>
        }
      >
        <p className='mb-3 text-sm'>
          {t('pages.sitters.saveSearchModal.intro')}
        </p>
        {savedSearchIdParam != null && (
          <fieldset className='mb-4 flex flex-wrap gap-x-6 gap-y-2 text-sm'>
            <legend className='sr-only'>{t('pages.sitters.saveSearchModal.modeLegend')}</legend>
            <label className='flex items-center gap-2 cursor-pointer'>
              <input
                type='radio'
                name='save-search-mode'
                value='update'
                checked={saveSearchMode === 'update'}
                onChange={(): void => setSaveSearchMode('update')}
              />
              <span>{t('pages.sitters.saveSearchModal.updateOption')}</span>
            </label>
            <label className='flex items-center gap-2 cursor-pointer'>
              <input
                type='radio'
                name='save-search-mode'
                value='create'
                checked={saveSearchMode === 'create'}
                onChange={(): void => setSaveSearchMode('create')}
              />
              <span>{t('pages.sitters.saveSearchModal.newOption')}</span>
            </label>
          </fieldset>
        )}
        <div className='grid grid-cols-1 md:grid-cols-12 gap-6'>
          <div className='md:col-span-7'>
            <label htmlFor='save-search-name' className='block font-semibold mb-1'>
              {t('pages.sitters.saveSearchModal.nameLabel')}
            </label>
            <input
              id='save-search-name'
              type='text'
              className='form-control w-full border border-neutral-300 rounded px-3 py-2 mb-3'
              placeholder={t('pages.sitters.saveSearchModal.namePlaceholder')}
              value={saveSearchName}
              onChange={(e): void => setSaveSearchName(e.target.value)}
            />
            {notificationTypes.length > 0 && (
              <div className='mb-3'>
                <div className='font-semibold mb-1'>
                  {t('pages.sitters.saveSearchModal.notifyHeading')}
                </div>
                <label htmlFor='email-frequency' className='block text-sm mb-1'>
                  {t('pages.sitters.saveSearchModal.emailLabel')}
                </label>
                <select
                  id='email-frequency'
                  className='form-control w-full border border-neutral-300 rounded px-2 py-1'
                  value={selectedNotificationTypeId ?? ''}
                  onChange={(e): void =>
                    setSelectedNotificationTypeId(Number(e.target.value) || null)
                  }
                >
                  {notificationTypes.map((type) => (
                    <option key={type.id} value={type.id}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
          <div className='md:col-span-5 text-sm'>
            <h4 className='font-semibold mb-2'>
              {t('pages.sitters.saveSearchModal.appliedFilters', {
                count: Array.from(searchParams.keys()).filter((k) => k !== 'page').length,
              })}
            </h4>
            {activeVerticalId != null && (
              <div className='mb-1'>
                <span className='font-semibold'>
                  {t('pages.sitters.saveSearchModal.typeOfCare')}
                </span>{' '}
                <span>{VERTICAL_BY_ID[activeVerticalId] ?? ''}</span>
              </div>
            )}
            {searchTypeCode && (
              <div className='mb-1'>
                <span className='font-semibold'>
                  {t('pages.sitters.saveSearchModal.typeOfJob')}
                </span>{' '}
                <span>
                  {SITTER_SEARCH_TYPES.find((s) => s.code === searchTypeCode)?.label ?? ''}
                </span>
              </div>
            )}
            {(searchParams.get('city') || searchParams.get('state')
              || parent?.address?.city) && (
              <div className='mb-1'>
                <span className='font-semibold'>
                  {t('pages.sitters.saveSearchModal.locationLabel')}
                </span>{' '}
                <span>
                  {[
                    searchParams.get('city') ?? parent?.address?.city,
                    searchParams.get('state') ?? parent?.address?.state,
                  ].filter(Boolean).join(', ')}
                </span>
              </div>
            )}
            <div className='mb-1'>
              <span className='font-semibold'>
                {t('pages.sitters.saveSearchModal.distanceLabel')}
              </span>{' '}
              <span>
                {t('pages.sitters.saveSearchModal.distanceMiles', {
                  count: Number(searchParams.get('distance')) || 25,
                })}
              </span>
            </div>
            {Number(searchParams.get('numberOfChildren')) > 0 && (
              <div className='mb-1'>
                <span className='font-semibold'>
                  {t('pages.sitters.saveSearchModal.numberReceivingCare')}
                </span>{' '}
                <span>{searchParams.get('numberOfChildren')}</span>
              </div>
            )}
            {searchParams.get('maximumRate') && (
              <div className='mb-1'>
                <span className='font-semibold'>
                  {t('pages.sitters.saveSearchModal.rateLabel')}
                </span>{' '}
                <span>
                  {t('pages.sitters.saveSearchModal.rateUnder', {
                    rate: searchParams.get('maximumRate'),
                  })}
                </span>
              </div>
            )}
          </div>
        </div>
      </Modal>
    </div>
  );
});
